import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  Building2,
  CreditCard,
  Search,
  Wallet,
  Copy,
  Check,
  Smartphone,
  Eye,
  X,
  Loader2,
  FileText,
  Image,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { PAKISTAN_BANKS } from '../../context/AppContext';
import { useBrokerData } from '../../context/BrokerDataContext';
import { getBrokerFromPath } from '../../config/brokerConfig';
import { adminDecisionNotificationService } from '../../services/emailService';
import './AccountVerification.css';

function AccountVerification() {
  const location = useLocation();
  const brokerId = getBrokerFromPath(location.pathname);

  // Use broker-specific data from BrokerDataContext
  const {
    pendingRegistrations: pendingAccountRegistrations,
    approvePendingRegistration: brokerApprovePendingRegistration,
    rejectPendingRegistration: brokerRejectPendingRegistration,
    isLoading
  } = useBrokerData();

  // Wrapper functions that include brokerId
  const approvePendingRegistration = async (id, comment) => {
    return brokerApprovePendingRegistration(id);
  };

  const rejectPendingRegistration = async (id, reason) => {
    return brokerRejectPendingRegistration(id, reason);
  };

  // Debug log
  console.log('[AccountVerification] pendingAccountRegistrations:', pendingAccountRegistrations);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [approveComment, setApproveComment] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);

  const handleCopy = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setShowCopyToast(true);
      setTimeout(() => {
        setCopiedField(null);
        setShowCopyToast(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getBankLogo = (bankCode) => {
    const bank = PAKISTAN_BANKS.find(b => b.code === bankCode);
    const isWallet = bank?.type === 'wallet';
    const logoUrl = bank?.logo;

    return (
      <div className="bank-logo-wrapper">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={bank?.name || bankCode}
            className="bank-logo"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="bank-logo-fallback" style={{ display: logoUrl ? 'none' : 'flex' }}>
          {isWallet ? <Wallet size={20} /> : <Building2 size={20} />}
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper to check if account is a wallet
  const isWalletAccount = (account) => {
    const bank = PAKISTAN_BANKS.find(b => b.code === account.bankCode);
    return bank?.type === 'wallet';
  };

  // Filter accounts based on search and filter status
  const filteredAccounts = useMemo(() => {
    console.log('[AccountVerification] Filtering accounts:', {
      total: pendingAccountRegistrations.length,
      searchQuery,
      filterStatus
    });

    const filtered = pendingAccountRegistrations
      .filter(account => {
        // Search filter - if search is empty, match all
        const matchesSearch = !searchQuery ||
          account.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          account.cnic?.includes(searchQuery) ||
          account.bankName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          account.iban?.includes(searchQuery);

        // Status filter
        let matchesStatus = true;
        if (filterStatus === 'wallets') {
          matchesStatus = isWalletAccount(account);
        } else if (filterStatus === 'banks') {
          matchesStatus = !isWalletAccount(account);
        }

        return matchesSearch && matchesStatus;
      })
      // Sort by date - newest first
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

    console.log('[AccountVerification] Filtered accounts:', filtered.length, filtered);
    return filtered;
  }, [pendingAccountRegistrations, searchQuery, filterStatus]);

  const handleApproveClick = (id, e) => {
    if (e) e.stopPropagation();
    setApprovingId(id);
    setApproveComment('');
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    if (approvingId) {
      setProcessing(true);
      try {
        // Find the account being approved
        const account = pendingAccountRegistrations.find(a => a.id === approvingId);

        await approvePendingRegistration(approvingId, approveComment);

        // Send email notification in background (non-blocking for speed)
        const accountEmail = account?.email || account?.userEmail;
        if (account && accountEmail) {
          adminDecisionNotificationService.sendAccountDecisionNotification({
            email: accountEmail,
            userName: account.userName,
            bankName: account.bankName,
            iban: account.iban,
            accountNumber: account.accountNumber,
            accountTitle: account.accountTitle,
            submissionId: account.id,
            decision: 'approved',
            adminComment: approveComment,
            documentBase64: account.documentBase64,
            documentName: account.documentName,
            documentType: account.documentType,
          }).catch(err => console.error('[Admin] Failed to send approval email:', err));
        }

        // Close modal immediately
        setShowApproveModal(false);
        setApprovingId(null);
        setApproveComment('');
        setSelectedAccount(null);
      } catch (error) {
        console.error('Error approving:', error);
      } finally {
        setProcessing(false);
      }
    }
  };

  const handleRejectClick = (id, e) => {
    if (e) e.stopPropagation();
    setRejectingId(id);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (rejectingId && rejectReason.trim()) {
      setProcessing(true);
      try {
        // Find the account being rejected
        const account = pendingAccountRegistrations.find(a => a.id === rejectingId);

        await rejectPendingRegistration(rejectingId, rejectReason);

        // Send email notification in background (non-blocking for speed)
        const accountEmail = account?.email || account?.userEmail;
        if (account && accountEmail) {
          adminDecisionNotificationService.sendAccountDecisionNotification({
            email: accountEmail,
            userName: account.userName,
            bankName: account.bankName,
            iban: account.iban,
            accountNumber: account.accountNumber,
            accountTitle: account.accountTitle,
            submissionId: account.id,
            decision: 'rejected',
            rejectionReason: rejectReason,
            documentBase64: account.documentBase64,
            documentName: account.documentName,
            documentType: account.documentType,
          }).catch(err => console.error('[Admin] Failed to send rejection email:', err));
        }

        // Close modal immediately
        setShowRejectModal(false);
        setRejectingId(null);
        setRejectReason('');
        setSelectedAccount(null);
      } catch (error) {
        console.error('Error rejecting:', error);
      } finally {
        setProcessing(false);
      }
    }
  };

  const stats = useMemo(() => ({
    total: pendingAccountRegistrations.length,
    wallets: pendingAccountRegistrations.filter(a => isWalletAccount(a)).length,
    banks: pendingAccountRegistrations.filter(a => !isWalletAccount(a)).length,
  }), [pendingAccountRegistrations]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="account-verification-page">
        <div className="loading-container">
          <Loader2 size={48} className="spinning" />
          <h3>Loading Accounts...</h3>
          <p>Fetching pending registrations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-verification-page">
      {/* Page Header */}
      <div className="page-header">
        <h1>Account Verification</h1>
        <p>Review and approve pending bank account registrations</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-row">
        <div className="stat-card pending" style={{ '--card-index': 0 }}>
          <div className="stat-icon">
            <Clock size={26} strokeWidth={1.5} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Pending Review</span>
          </div>
          <div className="stat-decoration">
            <Clock size={80} strokeWidth={0.5} />
          </div>
        </div>
        <div className="stat-card wallet" style={{ '--card-index': 1 }}>
          <div className="stat-icon">
            <Smartphone size={26} strokeWidth={1.5} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.wallets}</span>
            <span className="stat-label">Mobile Wallets</span>
          </div>
          <div className="stat-decoration">
            <Smartphone size={80} strokeWidth={0.5} />
          </div>
        </div>
        <div className="stat-card bank" style={{ '--card-index': 2 }}>
          <div className="stat-icon">
            <Building2 size={26} strokeWidth={1.5} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.banks}</span>
            <span className="stat-label">Bank Accounts</span>
          </div>
          <div className="stat-decoration">
            <Building2 size={80} strokeWidth={0.5} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <CardContent>
          <div className="filters-row">
            <div className="search-wrapper">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search by name, CNIC, bank or IBAN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                onClick={() => setFilterStatus('all')}
              >
                All ({stats.total})
              </button>
              <button
                className={`filter-btn ${filterStatus === 'wallets' ? 'active' : ''}`}
                onClick={() => setFilterStatus('wallets')}
              >
                Wallets ({stats.wallets})
              </button>
              <button
                className={`filter-btn ${filterStatus === 'banks' ? 'active' : ''}`}
                onClick={() => setFilterStatus('banks')}
              >
                Banks ({stats.banks})
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      <Card className="accounts-list-card">
        <CardContent>
          <div className="accounts-list">
            {filteredAccounts.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={48} />
                <h3>No Pending Accounts</h3>
                <p>All account registrations have been reviewed.</p>
              </div>
            ) : (
              filteredAccounts.map((account) => (
                <div
                  key={account.id}
                  className="account-item"
                >
                  <div className="account-main">
                    {/* Left: Account Info */}
                    <div className="account-info">
                      <div className="account-ref">
                        {getBankLogo(account.bankCode)}
                        <div className="account-ref-text">
                          <span className="ref-label">{isWalletAccount(account) ? 'Wallet:' : 'Bank:'}</span>
                          <span className="ref-value">{account.bankName}</span>
                        </div>
                      </div>
                      <div className="account-user">
                        <User size={14} />
                        <span>{account.userName}</span>
                      </div>
                      <div className="account-cnic-row">
                        <CreditCard size={14} />
                        <span>{account.cnic}</span>
                      </div>
                    </div>

                    {/* Middle: Account Number */}
                    <div className="account-number-section">
                      <span className="account-number-value">
                        {account.accountNumber || account.iban}
                      </span>
                      <span className="account-date">
                        {formatDate(account.submittedAt)}
                      </span>
                    </div>

                    {/* Status */}
                    <div className="account-status">
                      <span className={`type-badge ${isWalletAccount(account) ? 'wallet' : 'bank'}`}>
                        {isWalletAccount(account) ? 'Mobile Wallet' : 'Bank Account'}
                      </span>
                      <span className="status-badge pending">Pending Review</span>
                    </div>

                    {/* Actions */}
                    <div className="account-actions">
                      <Button
                        variant="ghost"
                        size="small"
                        icon={Eye}
                        onClick={() => setSelectedAccount(account)}
                      >
                        View
                      </Button>
                      <Button
                        variant="success"
                        size="small"
                        icon={CheckCircle}
                        onClick={(e) => handleApproveClick(account.id, e)}
                        loading={processing && approvingId === account.id}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="danger"
                        size="small"
                        icon={XCircle}
                        onClick={(e) => handleRejectClick(account.id, e)}
                        loading={processing && rejectingId === account.id}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Account Detail Modal */}
      {selectedAccount && (
        <div className="modal-overlay" onClick={() => setSelectedAccount(null)}>
          <div className="modal account-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Account Details</h2>
              <button className="modal-close" onClick={() => setSelectedAccount(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Full Name</span>
                <span
                  className={`detail-value copyable ${copiedField === `name-${selectedAccount.id}` ? 'copied' : ''}`}
                  onClick={() => handleCopy(selectedAccount.userName, `name-${selectedAccount.id}`)}
                >
                  {selectedAccount.userName}
                  {copiedField === `name-${selectedAccount.id}` ? <Check size={12} /> : <Copy size={12} />}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">CNIC</span>
                <span
                  className={`detail-value mono copyable ${copiedField === `cnic-${selectedAccount.id}` ? 'copied' : ''}`}
                  onClick={() => handleCopy(selectedAccount.cnic, `cnic-${selectedAccount.id}`)}
                >
                  {selectedAccount.cnic}
                  {copiedField === `cnic-${selectedAccount.id}` ? <Check size={12} /> : <Copy size={12} />}
                </span>
              </div>

              <div className="detail-divider"></div>

              <div className="detail-row bank-detail-row">
                <span className="detail-label">{isWalletAccount(selectedAccount) ? 'Wallet' : 'Bank'}</span>
                <div
                  className={`detail-value with-logo copyable ${copiedField === `bank-${selectedAccount.id}` ? 'copied' : ''}`}
                  onClick={() => handleCopy(selectedAccount.bankName, `bank-${selectedAccount.id}`)}
                >
                  {getBankLogo(selectedAccount.bankCode)}
                  <span>{selectedAccount.bankName}</span>
                  {copiedField === `bank-${selectedAccount.id}` ? <Check size={12} /> : <Copy size={12} />}
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">Account Number</span>
                <span
                  className={`detail-value mono copyable ${copiedField === `iban-${selectedAccount.id}` ? 'copied' : ''}`}
                  onClick={() => handleCopy(selectedAccount.accountNumber || selectedAccount.iban, `iban-${selectedAccount.id}`)}
                >
                  {selectedAccount.accountNumber || selectedAccount.iban}
                  {copiedField === `iban-${selectedAccount.id}` ? <Check size={12} /> : <Copy size={12} />}
                </span>
              </div>

              {/* Review Note */}
              {selectedAccount.reviewNote && (
                <>
                  <div className="detail-divider"></div>
                  <div className="detail-row">
                    <span className="detail-label">Review Note</span>
                    <span className="detail-value review-note">{selectedAccount.reviewNote}</span>
                  </div>
                </>
              )}

              {/* Document/Screenshot Section */}
              <div className="detail-divider"></div>
              <div className="document-section">
                <div className="document-header">
                  <FileText size={16} />
                  <span>{isWalletAccount(selectedAccount) ? 'App Screenshot' : 'Verification Document'}</span>
                </div>

                {selectedAccount.documentBase64 ? (
                  <div className="document-preview">
                    <img
                      src={selectedAccount.documentBase64}
                      alt={isWalletAccount(selectedAccount) ? 'App Screenshot' : 'IBAN Document'}
                      className="document-image"
                      onClick={() => {
                        setZoomedImage(selectedAccount.documentBase64);
                        setImageZoom(1);
                        setShowImageModal(true);
                      }}
                    />
                    <div className="document-info">
                      <span className="document-name">{selectedAccount.documentName || 'Document'}</span>
                      <Button
                        variant="outline"
                        size="small"
                        icon={ZoomIn}
                        onClick={() => {
                          setZoomedImage(selectedAccount.documentBase64);
                          setImageZoom(1);
                          setShowImageModal(true);
                        }}
                      >
                        View Full Size
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="no-document">
                    <Image size={32} />
                    <p>No document uploaded</p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <Button
                variant="danger"
                size="small"
                icon={XCircle}
                onClick={() => handleRejectClick(selectedAccount.id)}
                loading={processing}
              >
                Reject
              </Button>
              <Button
                variant="success"
                size="small"
                icon={CheckCircle}
                onClick={() => handleApproveClick(selectedAccount.id)}
                loading={processing}
              >
                Approve
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="approve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header approve">
              <CheckCircle size={24} className="modal-icon" />
              <h3>Approve Account</h3>
            </div>
            <div className="modal-body">
              <p>Add an optional comment for approving this account.</p>
              <textarea
                placeholder="Enter approval comment (optional)..."
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowApproveModal(false)} disabled={processing}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleApproveConfirm}
                loading={processing}
              >
                Confirm Approval
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Toast */}
      {showCopyToast && (
        <div className="copy-toast">
          Copied to clipboard!
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header reject">
              <XCircle size={24} className="modal-icon" />
              <h3>Reject Account</h3>
            </div>
            <div className="modal-body">
              <p>Please provide a reason for rejecting this account registration.</p>
              <textarea
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowRejectModal(false)} disabled={processing}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
                loading={processing}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {showImageModal && zoomedImage && (
        <div className="modal-overlay image-zoom-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="zoom-header">
              <div className="zoom-controls">
                <button
                  className="zoom-btn"
                  onClick={() => setImageZoom(prev => Math.max(0.5, prev - 0.25))}
                  disabled={imageZoom <= 0.5}
                >
                  <ZoomOut size={20} />
                </button>
                <span className="zoom-level">{Math.round(imageZoom * 100)}%</span>
                <button
                  className="zoom-btn"
                  onClick={() => setImageZoom(prev => Math.min(3, prev + 0.25))}
                  disabled={imageZoom >= 3}
                >
                  <ZoomIn size={20} />
                </button>
              </div>
              <button className="modal-close" onClick={() => setShowImageModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="zoom-image-container">
              <img
                src={zoomedImage}
                alt="Document Full Size"
                style={{ transform: `scale(${imageZoom})` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountVerification;
